import type { DepositIbanInfo, DepositIdentifierDetails } from "@payping/api";
import { Bank, Col, Descriptions, Drawer, LegacyCard, Row, Space, Tag, Typography } from "@payping/web-components";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { Dispatch, FC, SetStateAction } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { formatIBAN } from "@/utils/formatIBAN";
import { formatCardNumber } from "@/utils/formatCardNumber";
import { getBankInfoFromCardNumber, getBankInfoFromSheba } from "@payping/utilities-shared/bank";

interface Props {
  data?: DepositIdentifierDetails;
  setData: Dispatch<SetStateAction<DepositIdentifierDetails | undefined>>;
  accountData?: DepositIbanInfo;
}

export const IDDetails: FC<Props> = (props) => {
  const { data, setData } = props;
  const { t } = useAppTranslation();
  const jalaliDateTime = toJalali({ date: data?.createdOn, toFormat: dateFormats.ui.jalaliDateTime }).string;
  const [jalaliDate, jalaliTime] = jalaliDateTime.split(" ");

  const formattedResult = `${jalaliTime}  |  ${jalaliDate}`;

  return (
    <Drawer
      title={t("settlement.id.idDetail")}
      placement={"left"}
      open={Boolean(data)}
      onClose={() => setData(undefined)}
      height={"100%"}
      width={400}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Descriptions className="tw:w-full" column={1} bordered>
            <Descriptions.Item label={t("settlement.id.payer")}>
              {data?.isLegal ? data?.title : `${data?.firstName} ${data?.lastName}`}
              <Tag className="tw:mr-4!"> {data?.isLegal ? `${t("legal")}` : `${t("natural")}`}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("settlement.id.createDate")}>{formattedResult}</Descriptions.Item>
            <Descriptions.Item label={t("settlement.id.code")}>{data?.depositId}</Descriptions.Item>
            {data?.isLegal ? (
              <>
                <Descriptions.Item label={t("bNationalCode")}>{data?.businessNationalId}</Descriptions.Item>
                <Descriptions.Item label={t("settings.business.registrationDate")}>
                  {toJalali({ date: data?.registerDate, toFormat: dateFormats.ui.jalaliDate }).string}
                </Descriptions.Item>
              </>
            ) : (
              <>
                <Descriptions.Item label={t("nationalCode")}>{data?.nationalCode}</Descriptions.Item>
                <Descriptions.Item label={t("birthDate")}>
                  {toJalali({ date: data?.birthDate, toFormat: dateFormats.ui.jalaliDate }).string}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label={t("mobile")}>{data?.phoneNumber}</Descriptions.Item>
            <Descriptions.Item label={t("description")}>{data?.description || "-"}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24}>
          {(data?.sourceIbans || []).map((item, index) => (
            <LegacyCard.Ant className={"tw:mb-4!"} key={index}>
              <Space orientation={"vertical"} className={"tw:w-full"} size={"small"}>
                <Typography.Text strong>{t("settlement.id.iban")}</Typography.Text>
                <Typography.Text className={"en-font"} strong>
                  {formatIBAN(item)}
                </Typography.Text>
                <Space>
                  {data?.sourceIbans?.map((iban, i) => (
                    <Space key={i}>
                      <Bank.Icon showTooltip bankName={getBankInfoFromSheba(iban)?.name} />
                      <Typography.Text>{getBankInfoFromSheba(iban)?.persianName}</Typography.Text>
                    </Space>
                  ))}
                </Space>
              </Space>
            </LegacyCard.Ant>
          ))}
        </Col>
        <Col xs={24}>
          {(data?.sourceCardNumbers || []).map((item, index) => (
            <LegacyCard.Ant className={"tw:mb-4!"} key={index}>
              <Space orientation={"vertical"} className={"tw:w-full"} size={"small"}>
                <Typography.Text strong>{t("settlement.id.cardNumber")}</Typography.Text>
                <span className={"tw:align-start tw:flex"}>
                  <div className={"ltr"}>
                    <Typography.Text className={"en-font"} strong>
                      {formatCardNumber(item)}
                    </Typography.Text>
                  </div>
                </span>
                <Space>
                  {data?.sourceCardNumbers?.map((card, i) => (
                    <Space key={i}>
                      <Bank.Icon showTooltip bankName={getBankInfoFromCardNumber(card)?.name} />
                      <Typography.Text>{getBankInfoFromCardNumber(card)?.persianName}</Typography.Text>
                    </Space>
                  ))}
                </Space>
              </Space>
            </LegacyCard.Ant>
          ))}
        </Col>
      </Row>
    </Drawer>
  );
};
